import React, { Component, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import AdminLayout from './components/AdminLayout/AdminLayout';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PDV from './pages/PDV';
import Produtos from './pages/Produtos';
import Estoque from './pages/Estoque';
import Encomendas from './pages/Encomendas';
import Clientes from './pages/Clientes';
import Relatorios from './pages/Relatorios';
import Pedidos from './pages/Pedidos';
import FechamentoCaixa from './pages/FechamentoCaixa';
import Perfil from './pages/Perfil';
import AdminEstabelecimentos from './pages/Admin/Estabelecimentos';
import AdminUsuarios from './pages/Admin/Usuarios';
import Funcionarios from './pages/Funcionarios';
import NotFound from './pages/NotFound';
import SaibaMais from './pages/SaibaMais';
import Cadastro from './pages/Cadastro';

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Erro inesperado na aplicação:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Algo deu errado</h1>
            <p className="text-gray-600 mb-4">Ocorreu um erro inesperado. Tente recarregar a página.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700"
            >
              Recarregar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider queryClient={queryClient}>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />

          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/cadastro" element={<Cadastro />} />
            <Route path="/saiba-mais" element={<SaibaMais />} />

            {/* Rotas de administrador */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/estabelecimentos" replace />} />
              <Route path="estabelecimentos" element={<AdminEstabelecimentos />} />
              <Route path="usuarios" element={<AdminUsuarios />} />
              <Route path="funcionarios" element={<Funcionarios />} />
            </Route>

            {/* Rotas de gestão do estabelecimento */}
            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="pdv" element={<PDV />} />
              <Route path="produtos" element={<Produtos />} />
              <Route path="estoque" element={<Estoque />} />
              <Route path="encomendas" element={<Encomendas />} />
              <Route path="clientes" element={<Clientes />} />
              <Route path="pedidos" element={<Pedidos />} />
              <Route path="relatorios" element={<Relatorios />} />
              <Route path="fechamento-caixa" element={<FechamentoCaixa />} />
              <Route path="funcionarios" element={<Funcionarios />} />
              <Route path="perfil" element={<Perfil />} />
            </Route>

            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
